import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckoutIntents } from 'checkout-intents';
import { checkoutIntentsClient } from 'app/checkout-intents/client';
import * as Sentry from '@sentry/react';

const fetchCheckoutIntents = async (
  params: CheckoutIntents.CheckoutIntentListParams,
): Promise<CheckoutIntents.CheckoutIntentsCursorPagination> => {
  const client = checkoutIntentsClient.get();
  if (!client) {
    throw new Error('API client not initialized');
  }

  return client.checkoutIntents.list(params);
};

const fetchCheckoutIntent = async (id: string): Promise<CheckoutIntents.CheckoutIntent> => {
  const client = checkoutIntentsClient.get();
  if (!client) {
    throw new Error('API client not initialized');
  }

  return client.checkoutIntents.retrieve(id);
};

export const fetchCheckoutIntentByOrderId = async (
  orderId: string,
): Promise<CheckoutIntents.CheckoutIntent | null> => {
  const client = checkoutIntentsClient.get();
  if (!client) {
    throw new Error('API client not initialized');
  }

  return client.get<CheckoutIntents.CheckoutIntentsResource.CheckoutIntent | null>(
    `/api/v1/checkout-intents/marketplace/order/${encodeURIComponent(orderId)}`,
  );
};

export const useCheckoutIntents = (params: CheckoutIntents.CheckoutIntentListParams) => {
  const queryClient = useQueryClient();

  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['checkoutIntents', params],
    queryFn: () =>
      fetchCheckoutIntents(params).catch((e) => {
        console.error('Error fetching checkout intents:', e);
        Sentry.captureException(new Error('Error fetching checkout intents', { cause: e }));
        throw e;
      }),
    enabled: !!checkoutIntentsClient.get(),
  });

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['checkoutIntents'] });
  };

  return {
    checkoutIntents: data?.data ?? [],
    pageInfo: data?.pageInfo,
    loading,
    error: error as Error | null,
    refetch,
  };
};

export const useCheckoutIntent = (id: string | undefined) => {
  const {
    data: checkoutIntent,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['checkoutIntent', id],
    queryFn: () => fetchCheckoutIntent(id as string),
    enabled: !!id && id.startsWith('ci_') && !!checkoutIntentsClient.get(),
  });

  return {
    checkoutIntent,
    loading,
    error: error as Error | null,
  };
};
